import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hfContent, setHfContent] = useState(null);

  const [contactContent, setContactContent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/content/header_footer`).then(res => {
      if (res.data && Object.keys(res.data).length > 0) setHfContent(res.data);
    }).catch(() => {});
    axios.get(`${API}/api/public/content/contact`).then(res => {
      if (res.data && Object.keys(res.data).length > 0) setContactContent(res.data);
    }).catch(() => {});
  }, []);

  const contactEmail = hfContent?.footer_email || '';
  const contactAddress = (() => {
    const val = hfContent?.footer_address;
    if (!val) return '';
    if (typeof val === 'object') return val[lang] || val.en || '';
    return val;
  })();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/public/contact/submit`, formData);
      toast.success(t('contact.successMessage'));
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(t('contact.errorMessage') || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactImage = contactContent?.image || "";

  useEffect(() => { document.title = 'VIYERO | İletişim'; return () => { document.title = 'VIYERO | Ergonomic Solutions'; }; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('contact.infoTitle')}
              </h2>
              
              <div className="space-y-6 mb-12">
                {contactEmail && (
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.email')}</h3>
                    <a 
                      href={`mailto:${contactEmail}`}
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      {contactEmail}
                    </a>
                  </div>
                </div>
                )}

                {contactAddress && (
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.address')}</h3>
                    <p className="text-gray-600">
                      {contactAddress}
                    </p>
                  </div>
                </div>
                )}
              </div>

              {contactImage && (
              <div className="hidden lg:block rounded-2xl overflow-hidden">
                <img
                  src={contactImage}
                  alt="Contact"
                  className="w-full h-full object-cover"
                />
              </div>
              )}
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('contact.formTitle')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">{t('contact.name')} *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.namePlaceholder')}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">{t('contact.emailLabel')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.emailPlaceholder')}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="subject">{t('contact.subject')} *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.subjectPlaceholder')}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="message">{t('contact.message')} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.messagePlaceholder')}
                    rows={6}
                    className="mt-1 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {isSubmitting ? t('contact.sending') : (
                    <>
                      {t('contact.sendButton')}
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
